import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Chip,
  Modal,
  ModalDialog,
  ModalClose,
  Alert,
  CircularProgress,
} from '@mui/joy';
import { Add } from '@mui/icons-material';
import { tagService } from '../services/tagService';
import { Tag, ScheduleTagWithDetails } from '../types/tag';
import { TagSelector } from './TagSelector';
import { TagChip } from './TagChip';
import { useAuth } from '../context/AuthContext';

interface ScheduleTagManagerProps {
  scheduleId: number;
  onTagsChange?: () => void;
  readOnly?: boolean;
  preFetchedTags?: any[];
}

export const ScheduleTagManager: React.FC<ScheduleTagManagerProps> = ({
  scheduleId,
  onTagsChange,
  readOnly = false,
  preFetchedTags,
}) => {
  const { user, permissions } = useAuth();
  const [tags, setTags] = useState<ScheduleTagWithDetails[]>([]);
  const [loading, setLoading] = useState(!preFetchedTags);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (preFetchedTags) {
      setTags(preFetchedTags);
      setLoading(false);
    } else {
      loadScheduleTags();
    }
  }, [scheduleId, preFetchedTags]);

  const loadScheduleTags = async () => {
    try {
      setLoading(true);
      setError(null);
      const scheduleTags = await tagService.getScheduleTags(scheduleId);
      setTags(scheduleTags);
    } catch (error) {
      console.error('Error loading schedule tags:', error);
      setError('Failed to load schedule tags');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTags = async () => {
    if (selectedTags.length === 0) return;

    try {
      setUpdating(true);
      // Get existing tag IDs
      const existingTagIds = tags.map(t => t.tag_id || t.tag?.id || t.id);
      // Get new tag IDs
      const newTagIds = selectedTags.map(tag => tag.id);
      // Merge existing and new tags, removing duplicates
      const allTagIds = [...new Set([...existingTagIds, ...newTagIds])];
      await tagService.bulkUpdateScheduleTags(scheduleId, allTagIds);
      await loadScheduleTags();
      setShowAddModal(false);
      setSelectedTags([]);
      onTagsChange?.();
    } catch (error) {
      console.error('Error adding tags:', error);
      setError('Failed to add tags to schedule');
    } finally {
      setUpdating(false);
    }
  };

  const handleRemoveTag = async (tagId: number) => {
    try {
      await tagService.removeTagFromSchedule(scheduleId, tagId);
      await loadScheduleTags();
      onTagsChange?.();
    } catch (error) {
      console.error('Error removing tag:', error);
      setError('Failed to remove tag from schedule');
    }
  };

  const canManageTags = permissions.includes('tags.assign') || 
                       permissions.includes('tags.remove');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size="sm" />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography level="body-sm" fontWeight="bold">
          Tags ({tags.length})
        </Typography>
        {!readOnly && canManageTags && (
          <Button
            size="sm"
            startDecorator={<Add />}
            onClick={() => setShowAddModal(true)}
          >
            Add Tags
          </Button>
        )}
      </Stack>

      {tags.length > 0 ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {tags.map((scheduleTag) => {
            // Handle both ScheduleTagWithDetails and plain tag objects
            const tag: Tag = (scheduleTag as ScheduleTagWithDetails).tag || (scheduleTag as any);
            const tagId = (scheduleTag as ScheduleTagWithDetails).tag_id || tag?.id || (scheduleTag as any).id;
            const canDelete = !readOnly && permissions.includes('tags.remove');
            
            return (
              <TagChip
                key={(scheduleTag as any).id}
                tag={tag}
                size="sm"
                deletable={canDelete}
                onDelete={canDelete ? () => handleRemoveTag(tagId) : undefined}
                onClick={() => {
                  // Could show tag details or navigate to tag management
                }}
              />
            );
          })}
        </Stack>
      ) : (
        <Typography level="body-sm" color="neutral">
          No tags assigned
        </Typography>
      )}

      {/* Add Tags Modal */}
      <Modal open={showAddModal} onClose={() => setShowAddModal(false)}>
        <ModalDialog size="md">
          <ModalClose />
          <Typography level="h4" component="h2" mb={2}>
            Add Tags to Schedule
          </Typography>

          <Stack spacing={3}>
            <TagSelector
              selectedTags={selectedTags}
              onTagsChange={setSelectedTags}
              multiple={true}
              placeholder="Search and select tags..."
            />

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => setShowAddModal(false)}
                disabled={updating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddTags}
                loading={updating}
                disabled={selectedTags.length === 0}
              >
                Add Tags
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
}; 